import React, {useRef, useState} from 'react';

import {FormattedMessage, IntlShape, useIntl} from 'react-intl';
import {Button, Scrollbar, SearchField, TableBar} from '@forecast-it/design-system';
import {FlexRow, Section} from '../../../../components/insights/invoicing/InvoicingPage.styled';
import Util from '../../../../forecast-app/shared/util/util';
import {createFragmentContainer, graphql} from 'react-relay';
import ProjectInvoicingTable from './ProjectInvoicingTable';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import moment from 'moment';
import {noop} from 'lodash';
import DeleteInvoiceMutation from '../../../../mutations/delete_invoice_mutation';
import {createToast} from '../../../../forecast-app/shared/components/toasts/toast';
import {synchronizeInvoices, unlinkInvoice} from './InvoiceExportUtil';
import {getFilterFunctions} from '../../../../forecast-app/shared/components/filters/filter_logic';
import InvoiceFilterButton from './InvoiceFilterButton';
import {getInitialOptions, handleChangedOptions} from '../../../../components/insights/invoicing/invoicing_eye_options';
import TheEye from '../../../../forecast-app/shared/components/headers/header-bar/TheEye';
import {useHistory} from 'react-router-dom';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import CompanySetupUtil from '../../../../forecast-app/shared/util/CompanySetupUtil';
import {hasModule} from '../../../../forecast-app/shared/util/ModuleUtil';
import {MODULE_TYPES} from '../../../../constants';
import {isSagePSAWithFinancialsAccess} from '../../../../forecast-app/shared/util/sage/SageRestrictionUtil';
import {isoDateToMomentNonUtc} from '../../../../forecast-app/shared/util/DateUtil';
import {ProjectInvoicingTableSection_viewer$data} from './__generated__/ProjectInvoicingTableSection_viewer.graphql';
import {ProjectInvoicingTableSection_company$data} from './__generated__/ProjectInvoicingTableSection_company.graphql';
import {ProjectInvoicingTableSection_project$data} from './__generated__/ProjectInvoicingTableSection_project.graphql';

type Props = {
	viewer: ProjectInvoicingTableSection_viewer$data;
	company: ProjectInvoicingTableSection_company$data;
	project: ProjectInvoicingTableSection_project$data;
	refetch: () => void;
	startDate: string;
	endDate: string;
};

type ExportCsvHandle = {exportCsv: () => void};

const ProjectInvoicingTableSection = ({viewer, company, project, refetch, startDate, endDate}: Props) => {
	const {formatMessage} = useIntl();
	const history = useHistory();

	const startDateMoment = isoDateToMomentNonUtc(startDate);
	const endDateMoment = isoDateToMomentNonUtc(endDate);

	const hasIntegrations =
		company.newXeroEnabled || company.quickbooksEnabled || company.economicEnabled || company.sageIntacctConnectionEnabled;

	const isSagePSA = hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED);

	const showSyncAndExport = hasIntegrations && (!isSagePSA || isSagePSAWithFinancialsAccess());

	const [filterFunctions, setFilterFunctions] = useState(
		getFilterFunctions(InvoiceFilterButton.InvoiceFiltersInLocalstorage)
	);

	const [theEyeOptions, setTheEyeOptions] = useState(getInitialOptions('project', hasIntegrations));
	const [searchFilterValue, setSearchFilterValue] = useState('');
	const handleEyeOptionsSelect = (_: unknown, __: unknown, ___: unknown, newOptions: unknown) => {
		setTheEyeOptions(handleChangedOptions(newOptions, 'project'));
	};

	const editInvoice = (companyInvoiceId: number | null | undefined) => {
		history.push(`${location.pathname}/I-${companyInvoiceId}`);
	};

	const openCreateNewInvoiceModal = (projectId: string, refetch: () => void) => {
		trackEvent('Create New Invoice Modal', 'Shown', {location: 'Project Page'});
		showModal({
			type: MODAL_TYPE.CREATE_INVOICE_V2_STANDALONE,
			refetch,
			onInvoiceCreation: noop,
			startDate: startDateMoment || moment().subtract(2, 'month'),
			endDate: endDateMoment || moment(),
			projectId: projectId,
			isProjectInvoicing: true,
		});
	};

	const deleteInvoice = (invoiceId: string, refetch: () => void, formatMessage: IntlShape['formatMessage']) => {
		const onSuccess = () => {
			// update state
			refetch();
			createToast({duration: 5000, message: formatMessage({id: 'invoice.invoice_deleted'})});
		};

		Util.CommitMutation(DeleteInvoiceMutation, {id: invoiceId}, onSuccess);
	};

	const exportCsvRef = useRef<ExportCsvHandle>();

	return (
		<>
			<TableBar title={formatMessage({id: 'common.invoices'})} titleSize={'1'} data-cy={'project-invoices-table-bar'}>
				<Button id={TableBar.ELEMENTS.DOWNLOAD} onPress={() => exportCsvRef?.current?.exportCsv()} />
				<SearchField id={TableBar.ELEMENTS.SEARCH} value={searchFilterValue} onChange={setSearchFilterValue} />
				<TheEye
					id={TableBar.ELEMENTS.EYE_OPTIONS}
					openRight={'true'}
					dataCy={'project_invoicing_eye'}
					options={theEyeOptions}
					onSelect={handleEyeOptionsSelect}
					useDesignSystemButton={true}
				/>
				<InvoiceFilterButton
					id={TableBar.ELEMENTS.FILTER}
					viewer={viewer}
					company={company}
					project={project}
					setFilterFunctions={setFilterFunctions}
				/>
				{showSyncAndExport ? (
					<Button
						id={'synchronize-invoices'}
						emphasis={'medium'}
						onPress={() =>
							synchronizeInvoices(
								company.newXeroEnabled,
								company.quickbooksEnabled,
								company.economicEnabled,
								company.sageIntacctConnectionEnabled
							)
						}
					>
						<FormattedMessage id="invoicing.synchronize" />
					</Button>
				) : (
					<></>
				)}

				{CompanySetupUtil.hasCreateInvoice() ? (
					<Button
						id={'create-new-invoice'}
						icon={'plus'}
						onPress={() => openCreateNewInvoiceModal(project.id, refetch)}
					>
						{formatMessage({id: 'invoicing.create_invoice'})}
					</Button>
				) : (
					<></>
				)}
			</TableBar>

			<Scrollbar hasFocusableContent>
				<Section marginBottom={55} data-userpilot={'invoices-table'}>
					<FlexRow growable>
						<ProjectInvoicingTable
							project={project}
							company={company}
							filterFunctions={filterFunctions}
							searchFilterValue={searchFilterValue}
							theEyeOptions={theEyeOptions}
							onEditInvoice={(companyInvoiceId: number | null | undefined) => editInvoice(companyInvoiceId)}
							onDeleteInvoice={(invoiceId: string) => deleteInvoice(invoiceId, refetch, formatMessage)}
							onUnlinkInvoice={(invoiceId: string) => unlinkInvoice(invoiceId, refetch, formatMessage)}
							exportCsvRef={exportCsvRef}
							showExport={showSyncAndExport}
						/>
					</FlexRow>
				</Section>
			</Scrollbar>
		</>
	);
};

export default createFragmentContainer(ProjectInvoicingTableSection, {
	viewer: graphql`
		fragment ProjectInvoicingTableSection_viewer on Viewer {
			...InvoiceFilterButton_viewer
		}
	`,
	company: graphql`
		fragment ProjectInvoicingTableSection_company on Company
		@argumentDefinitions(xeroInvoicingImprovements: {type: "Boolean!", defaultValue: false}) {
			newXeroEnabled
			quickbooksEnabled
			economicEnabled
			sageIntacctConnectionEnabled
			...ProjectInvoicingTable_company @arguments(xeroInvoicingImprovements: $xeroInvoicingImprovements)
			...ExportInvoiceButton_company @arguments(xeroInvoicingImprovements: $xeroInvoicingImprovements)
			...InvoiceFilterButton_company
		}
	`,
	project: graphql`
		fragment ProjectInvoicingTableSection_project on ProjectType {
			id
			...ProjectInvoicingTable_project
			...InvoiceFilterButton_project
		}
	`,
});
